import React from 'react';
import moment from 'moment';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { formatDate } from 'utils';
import type { NotificationType } from '@bb/domain/dist/types';
import { Icon as NewIcon } from '@bb/elements-web';
import ReactMarkdown from 'react-markdown';
import Badge from '../Badge';
import { Text } from '../Typography';
import Tooltip from '../Tooltip';
import useNotification from './useNotification';
import Icon from '../Icon';

import AppHeaderOverlay from './AppHeaderOverlay';
import { StyledHeaderItem } from './StyledComponents';
import NotificationFilters from './NotificationFilters';

type NotificationsProps = {
  iconSizes: {
    width: number;
    height: number;
  };
  isMobile?: boolean;
  closeMobileMenu: () => void;
  $height: number;
  showingsFeatureOn?: boolean;
};

const initialFilters: Record<NotificationType, boolean> = {
  manualCoop: true,
  coop: true,
  agent: true,
  announcement: true,
  transfer: true,
  keyCoop: false,
};

// Max height for IE Users
const StyledWrappedContainer = styled.div`
  span {
    max-height: 66px;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
`;

const Notifications: React.FC<NotificationsProps> = ({
  iconSizes,
  isMobile,
  closeMobileMenu,
  $height,
  showingsFeatureOn,
}) => {
  const history = useHistory();

  const {
    notifications,
    isLoading,
    countDirty,
    getNotificationType,
    getAvatarComponent,
    markAllRead,
  } = useNotification();

  const [filters, setFilters] = React.useState(initialFilters);
  const [showFilters, setShowFilters] = React.useState(false);
  const [overlayVisible, setOverlayVisible] = React.useState(false);

  const filtersCheckboxProps = (filter: keyof typeof filters) => ({
    checked: filters[filter],
    onChange: ({ target: { checked } }: CheckboxChangeEvent) =>
      setFilters({ ...filters, [filter]: checked }),
  });

  // doing local filtering
  const filteredNotifications = React.useMemo(
    () =>
      notifications?.filter(
        ({ notificationType }) =>
          filters[notificationType as keyof typeof initialFilters],
      ),
    [notifications, filters],
  );

  const getDateComponent = (date: string) => (
    <Tooltip title={moment(date).format('llll')} placement="bottom">
      {formatDate(date)}
    </Tooltip>
  );

  const close = () => {
    closeMobileMenu();
    setOverlayVisible(false);
  };

  return (
    <AppHeaderOverlay
      OverlayButton={
        isMobile ? (
          <StyledHeaderItem
            $height={$height}
            $isMobile={isMobile}
            $showBackgroundColor
            onClick={() => setOverlayVisible(visible => !visible)}
          >
            {showingsFeatureOn ? (
              <NewIcon icon="bellFilled" color="default" size={24} />
            ) : (
              <Icon type="bell" {...iconSizes} />
            )}
            <Text type="secondary">Notifications</Text>
            <div>
              <Badge color="primary" count={countDirty} overflowCount={99} />
            </div>
          </StyledHeaderItem>
        ) : (
          <Badge
            color="primary"
            count={countDirty}
            overflowCount={99}
            className="app-header-button"
          >
            <button className="app-header-button" onClick={close}>
              {showingsFeatureOn ? (
                <NewIcon
                  icon="bellFilled"
                  color="white"
                  size={24}
                  style={{ verticalAlign: '-0.3em' }}
                />
              ) : (
                <Icon type="bell" {...iconSizes} />
              )}
            </button>
          </Badge>
        )
      }
      overlayVisible={overlayVisible}
      closeMobileMenu={closeMobileMenu}
      setOverlayVisible={setOverlayVisible}
      title="Notifications"
      emptyDescription="You have no Notifications."
      pageUrl="/notification/list"
      isLoading={isLoading}
      subtitle={
        showFilters && (
          <NotificationFilters filtersCheckboxProps={filtersCheckboxProps} />
        )
      }
      options={[
        {
          icon: 'filter',
          label: 'Filters',
          onClick: () => setShowFilters(!showFilters),
        },
        {
          icon: 'check',
          label: 'Mark all as read',
          onClick: markAllRead,
        },
      ]}
      items={filteredNotifications?.map(notification => {
        const { subject, subjectMarkdown, createdAt, id, isDirty } =
          notification;
        const type = getNotificationType(notification);
        const AvatarComponent = getAvatarComponent(type, notification);
        const DateComponent = getDateComponent(createdAt);
        const body = sanitizeHtml(notification.body, {
          allowedTags: [],
          allowedAttributes: {},
        });

        return {
          onItemClick: () => {
            close();
            history.push(`/notification/list/${id}`);
          },
          AvatarComponent,
          DateComponent,
          MainComponent: (
            <div>
              <div>
                {subjectMarkdown ? (
                  <Text>
                    <ReactMarkdown>{subjectMarkdown}</ReactMarkdown>
                  </Text>
                ) : (
                  <Text>{subject}</Text>
                )}
              </div>
              {body && (
                <StyledWrappedContainer>
                  <Text>{body}</Text>
                </StyledWrappedContainer>
              )}
            </div>
          ),
          date: createdAt,
          showBadge: isDirty,
        };
      })}
    />
  );
};

export default Notifications;
